import type { AccountInfo } from '@azure/msal-browser-1p';
import type { MailboxInfo } from 'owa-client-types';
import { getItem } from 'owa-local-storage';
import type WebSessionType from 'owa-service/lib/contract/WebSessionType';
import { getAccountScopeUserSettings } from 'owa-session-store/lib/selectors/getAccountScopeUserSettings';
import { logStartCoreUsage } from 'owa-analytics-start';
import { getAuthConfig } from 'owa-auth-callbacks';
import { isBootFeatureEnabled } from 'owa-metatags/lib/isBootFeatureEnabled';

/**
 * Resolves the login_hint to pass to MSAL token requests using this priority order:
 *
 * 1. msalAccount id_token login_hint claim (opaque, preferred by STS) ✅ PREFERRED
 * 2. SessionSettings.LoginHint from session data ✅ PREFERRED
 * 3. In-memory cached login_hint claim from a previous login (via auth config, flighted) ✅ PREFERRED
 * 4. localStorage login_hint (persisted from a prior session, only when mailboxInfo is present)
 * 5. msalAccount.username (UPN fallback — to be removed) ⚠️ DEPRECATED
 * 6. EASI ID SMTP address (consumer-only, from NotManagedEmailAddresses)
 * 7. SessionSettings.LogonEmailAddress (shared mailbox scenarios)
 * 8. SessionSettings.UserPrincipalName (UPN fallback — to be removed) ⚠️ DEPRECATED
 * 9. mailboxInfo.userIdentity (last resort — to be removed) ⚠️ DEPRECATED
 * 10. Explicit username parameter (passed directly by caller)
 * 11. localStorage login_hint (ungated fallback when no other source matched)
 *
 * Migration Path:
 * - Phase 1 (Current): Infrastructure in place, telemetry for UPN fallbacks
 * - Phase 2 (Q2 2026): Remove UPN fallbacks (#5, #8, #9) after login_hint rollout completes
 * - Phase 3 (Q3 2026): Enforce login_hint-only, reject cross-account tokens
 *
 * See: LOGIN_HINT_CACHE_ARCHITECTURE.md for full details
 */
export function getLoginHint(
    msalAccount?: AccountInfo,
    mailboxInfo?: MailboxInfo,
    username?: string
): string | undefined {
    const sessionData = mailboxInfo
        ? getAccountScopeUserSettings(mailboxInfo)?.SessionSettings
        : undefined;

    return (
        msalAccount?.idTokenClaims?.login_hint ||
        sessionData?.LoginHint ||
        (isBootFeatureEnabled('auth-login-hint-from-auth-config') &&
            mailboxInfo &&
            getAuthConfig().getLoginHintClaim?.(mailboxInfo)) ||
        (mailboxInfo && getItem(self, 'login_hint')) ||
        logAndReturn(msalAccount?.username, 'msalAccount.username', mailboxInfo) ||
        logAndReturn(getEasiAddress(sessionData), 'NotManagedEmailAddresses', mailboxInfo) ||
        logAndReturn(sessionData?.LogonEmailAddress, 'LogonEmailAddress', mailboxInfo) ||
        logAndReturn(sessionData?.UserPrincipalName, 'UserPrincipalName', mailboxInfo) ||
        logAndReturn(mailboxInfo?.userIdentity, 'mailboxInfo.userIdentity', mailboxInfo) ||
        username ||
        getItem(self, 'login_hint') ||
        undefined
    );
}

function getEasiAddress(sessionData: any): string | undefined {
    if (sessionData?.WebSessionType !== 0 && sessionData?.NotManagedEmailAddresses?.length > 0) {
        return sessionData.NotManagedEmailAddresses[0];
    }
    return undefined;
}

/**
 * Logs telemetry when falling back to deprecated UPN-based identifiers.
 * This helps track migration progress away from UPN to login_hint.
 *
 * TODO: Remove this function and UPN fallbacks after login_hint
 * rollout completes (target: Q2 2026). Monitor MsalLoginHintFallback telemetry
 * to ensure coverage is >99% before removal.
 */
function logAndReturn(
    value: string | undefined,
    source: string,
    mailboxInfo?: MailboxInfo
): string | undefined {
    if (!value) {
        return undefined;
    }

    if (isBootFeatureEnabled('auth-login-hint-log-fallback')) {
        logStartCoreUsage('MsalLoginHintFallback', mailboxInfo, {
            fallbackSource: source,
        });
    }

    return value;
}
